"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { MarkedText } from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  bodyWords,
  buildLetter,
  HONORIFICS,
  LETTER_FORMATS,
  LETTER_PURPOSES,
  type LetterInput,
  letterBlanks,
  letterToHtml,
  letterToPrintHtml,
  letterToText,
  ONE_PAGE_BODY_WORDS,
  UNNAMED_KINDS,
  US_SIGNOFFS,
} from "@/lib/tool-kit/lit-essays/formal-letter";
import { printHtmlDocument } from "@/lib/tool-kit/print";

/** English strings. A translated content file overrides these through `ui`. */
export const FORMAL_LETTER_TEMPLATE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  formatLabel: "Letter format",
  formats: {
    usFull: "US full block",
    usModified: "US modified block",
    uk: "UK block",
  },
  formatHints: {
    usFull: "Every line starts at the left margin.",
    usModified:
      "The sender’s address, date, and closing start at the center; the rest stays left.",
    uk: "Everything left-aligned, with no full stops in Mr, Mrs, Ms, or Dr, and the date written day-month-year.",
  },
  purposeLabel: "What the letter is for",
  purposes: {
    inquiry: "Inquiry or request for information",
    complaint: "Complaint",
    application: "Application or cover letter",
    reference: "Request for a reference or recommendation",
    thanks: "Thank-you letter",
  },
  purposeHint: "Changes the prompts shown in empty paragraphs, not your text.",
  senderHeading: "Your details",
  senderNameLabel: "Your name",
  senderNamePlaceholder: "e.g. Jordan Lee",
  senderAddressLabel: "Your address — one line per line",
  senderAddressPlaceholder:
    "45 Elm Street\nAnytown, ST 12345\njordan.lee@example.com",
  senderAddressHint:
    "Put an email address or phone number on the last line if you want one.",
  dateLabel: "Date",
  dateHint:
    "Shown as March 12, 2026 in a US letter and 12 March 2026 in a UK letter.",
  today: "Use today’s date",
  recipientHeading: "Who you are writing to",
  recipientNameLabel: "Name",
  recipientNamePlaceholder: "e.g. Elena Rivera",
  recipientTitleLabel: "Job title (optional)",
  recipientTitlePlaceholder: "e.g. Director of Education",
  recipientOrganizationLabel: "Organization (optional)",
  recipientOrganizationPlaceholder: "e.g. Anytown Science Center",
  recipientAddressLabel: "Address — one line per line",
  recipientAddressPlaceholder: "120 Main Street\nAnytown, ST 12345",
  salutationHeading: "Salutation and sign-off",
  salutationKindLabel: "Do you know the recipient’s name?",
  salutationKinds: { named: "Yes, I know the name", unnamed: "No, I do not" },
  honorificLabel: "Title",
  honorifics: {
    Mr: "Mr",
    Ms: "Ms",
    Mrs: "Mrs",
    Mx: "Mx",
    Dr: "Dr",
    Prof: "Prof",
  },
  salutationNameLabel: "Name in the salutation",
  salutationNamePlaceholder: "e.g. Rivera",
  salutationNameHint:
    "Usually the surname. Never the first name in a formal letter.",
  unnamedLabel: "Salutation to use",
  unnamedKinds: {
    sirMadam: "Dear Sir or Madam",
    role: "Dear + a role (Hiring Manager)",
    whom: "To Whom It May Concern",
  },
  roleLabel: "Role or group",
  rolePlaceholder: "e.g. Hiring Manager",
  signoffLabel: "Complimentary close",
  usSignoffs: {
    sincerely: "Sincerely,",
    respectfully: "Respectfully,",
    regards: "Best regards,",
    truly: "Yours truly,",
  },
  signoffNotes: {
    us: "US letters close “Sincerely,” whether or not you know the name. Respectfully, Best regards, and Yours truly are also standard.",
    ukNamed:
      "A UK salutation with a name closes “Yours sincerely,”. The rule: name known, sincerely.",
    ukUnnamed:
      "A UK salutation without a name closes “Yours faithfully,”. The rule: no name, faithfully.",
  },
  messageHeading: "The message",
  subjectLabel: "Subject line (optional)",
  subjectPlaceholder: "e.g. School group tours for fall",
  openingLabel: "Opening paragraph",
  detailsLabel: "Middle paragraph",
  closingLabel: "Closing paragraph",
  paragraphPlaceholder: "Write the paragraph, or leave it to see the prompt.",
  enclosuresLabel: "Enclosures (optional)",
  enclosuresPlaceholder: "e.g. Class roster",
  openPunctuationLabel:
    "Open punctuation: no comma after the salutation or the close (UK style choice)",
  copiesLabel: "Copies to (optional)",
  copiesPlaceholder: "e.g. Mr. Okafor, Physics Teacher",
  letterHeading: "Your letter",
  copy: "Copy",
  copyPlain: "Copy as plain text",
  copied: "Copied!",
  download: "Download .txt",
  print: "Print",
  fileName: "formal-letter.txt",
  printTitle: "Formal letter",
  blanksLeft:
    "one:{count} blank left to fill in|other:{count} blanks left to fill in",
  blanksNone: "No blanks left.",
  lengthStatus: "one:Body: {count} word.|other:Body: {count} words.",
  lengthLong:
    "Longer than one page is likely. Business letters usually fit on one page, so trim to the essentials.",
  placeholders: {
    senderAddress: "Your address",
    date: "Date",
    recipientName: "Recipient’s name",
    recipientAddress: "Recipient’s address",
    salutationName: "Surname",
    role: "Role",
    subject: "Subject",
    senderName: "Your name",
    opening: "Opening",
    details: "Details",
    closing: "Closing",
  },
  salutation: {
    dear: "Dear",
    sirMadam: "Sir or Madam",
    whom: "To Whom It May Concern",
    format: "{dear} {honorific} {name}",
    dearNamed: {
      Mr: "Dear",
      Ms: "Dear",
      Mrs: "Dear",
      Mx: "Dear",
      Dr: "Dear",
      Prof: "Dear",
    },
    honorifics: {
      Mr: "Mr",
      Ms: "Ms",
      Mrs: "Mrs",
      Mx: "Mx",
      Dr: "Dr",
      Prof: "Prof",
    },
    honorificStop: ".",
  },
  signoffs: {
    us: {
      sincerely: "Sincerely,",
      respectfully: "Respectfully,",
      regards: "Best regards,",
      truly: "Yours truly,",
    },
    ukNamed: "Yours sincerely,",
    ukUnnamed: "Yours faithfully,",
  },
  subjectPrefix: "Re:",
  enclosures: "Enclosure:",
  copies: "cc:",
  bodyPrompts: {
    inquiry: {
      opening:
        "Opening: state who you are and what you are asking about, in one or two sentences.",
      details:
        "Details: give the background and the specific questions you need answered.",
      closing:
        "Closing: say what you would like them to do, thank them, and say how to reach you.",
    },
    complaint: {
      opening:
        "Opening: state what you bought or used, when, and that you are writing to complain.",
      details:
        "Details: describe what went wrong, with dates and reference numbers, and what you have already tried.",
      closing:
        "Closing: say exactly what you want (a refund, a repair, a reply) and by when.",
    },
    application: {
      opening: "Opening: name the position or program and where you saw it.",
      details:
        "Details: give two or three reasons you fit it, with specific evidence.",
      closing:
        "Closing: say you would welcome an interview, thank them, and say how to reach you.",
    },
    reference: {
      opening:
        "Opening: say who you are, how they know you, and that you are asking for a reference or recommendation.",
      details:
        "Details: give what they need — the job or program, the deadline, and what you hope they can speak to.",
      closing:
        "Closing: thank them, offer supporting materials, and repeat the deadline.",
    },
    thanks: {
      opening: "Opening: thank them for the specific thing they did.",
      details: "Details: say what it meant, or what difference it made.",
      closing:
        "Closing: close warmly, and say what happens next if anything does.",
    },
  },
  example: {
    format: "usFull",
    purpose: "inquiry",
    senderName: "Jordan Lee",
    senderAddress: "45 Elm Street\nAnytown, ST 12345\njordan.lee@example.com",
    date: "2026-03-12",
    recipientName: "Elena Rivera",
    recipientTitle: "Director of Education",
    recipientOrganization: "Anytown Science Center",
    recipientAddress: "120 Main Street\nAnytown, ST 12345",
    salutationKind: "named",
    honorific: "Ms",
    salutationName: "Rivera",
    unnamed: "sirMadam",
    role: "",
    subject: "School group tours for fall",
    opening:
      "I am a high school junior writing to ask whether the Science Center offers guided tours for school groups this fall.",
    details:
      "My physics class of 28 students would like to visit in October, on a weekday if possible. Could you tell me which programs are available for grades 11 and 12, what they cost, and how far in advance we should reserve a date?",
    closing:
      "Thank you for your help. I can be reached at jordan.lee@example.com.",
    usSignoff: "sincerely",
    enclosures: "Class roster",
    copies: "Mr. Okafor, Physics Teacher",
    openPunctuation: false,
  },
} as const;

type Strings = typeof FORMAL_LETTER_TEMPLATE_STRINGS;
type TextKey =
  | "senderName"
  | "senderAddress"
  | "date"
  | "recipientName"
  | "recipientTitle"
  | "recipientOrganization"
  | "recipientAddress"
  | "salutationName"
  | "role"
  | "subject"
  | "opening"
  | "details"
  | "closing"
  | "enclosures"
  | "copies";

const BLANK: LetterInput = {
  format: "usFull",
  purpose: "inquiry",
  senderName: "",
  senderAddress: "",
  date: "",
  recipientName: "",
  recipientTitle: "",
  recipientOrganization: "",
  recipientAddress: "",
  salutationKind: "named",
  honorific: "Ms",
  salutationName: "",
  unnamed: "sirMadam",
  role: "",
  subject: "",
  opening: "",
  details: "",
  closing: "",
  usSignoff: "sincerely",
  enclosures: "",
  copies: "",
  openPunctuation: false,
};

function localIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function signoffNote(input: LetterInput, strings: Strings): string {
  if (input.format !== "uk") {
    return strings.signoffNotes.us;
  }
  return input.salutationKind === "named"
    ? strings.signoffNotes.ukNamed
    : strings.signoffNotes.ukUnnamed;
}

export default function FormalLetterTemplateWidget() {
  const strings = useToolUi(FORMAL_LETTER_TEMPLATE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<LetterInput>(BLANK);
  const set =
    <K extends keyof LetterInput>(key: K) =>
    (value: LetterInput[K]) =>
      setInput((previous) => ({ ...previous, [key]: value }));
  const text = (key: TextKey) => set(key) as (value: string) => void;
  const blocks = buildLetter(input, strings, locale);
  const plain = letterToText(blocks);
  const blanks = letterBlanks(blocks);
  const words = bodyWords(input);
  const field = (
    key: TextKey,
    label: string,
    placeholder: string,
    hint?: string
  ) => (
    <TextField
      hint={hint}
      id={`fl-${key}`}
      label={label}
      onChange={text(key)}
      placeholder={placeholder}
      value={input[key]}
    />
  );
  const area = (
    key: TextKey,
    label: string,
    placeholder: string,
    rows = 3,
    hint?: string
  ) => (
    <TextAreaField
      hint={hint}
      id={`fl-${key}`}
      label={label}
      onChange={text(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              setInput((previous) => ({
                ...strings.example,
                format: previous.format,
              }))
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              setInput((previous) => ({ ...BLANK, format: previous.format }))
            }
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <Panel>
          <PillGroup
            label={strings.formatLabel}
            name="fl-format"
            onChange={set("format")}
            options={LETTER_FORMATS.map((value) => ({
              value,
              label: strings.formats[value],
            }))}
            value={input.format}
          />
          <p className={HINT}>{strings.formatHints[input.format]}</p>
          <SelectField
            hint={strings.purposeHint}
            id="fl-purpose"
            label={strings.purposeLabel}
            onChange={set("purpose")}
            options={LETTER_PURPOSES.map((value) => ({
              value,
              label: strings.purposes[value],
            }))}
            value={input.purpose}
          />
        </Panel>
        <Panel title={strings.senderHeading}>
          {field(
            "senderName",
            strings.senderNameLabel,
            strings.senderNamePlaceholder
          )}
          {area(
            "senderAddress",
            strings.senderAddressLabel,
            strings.senderAddressPlaceholder,
            3,
            strings.senderAddressHint
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[12rem] flex-1">
              <TextField
                hint={strings.dateHint}
                id="fl-date"
                label={strings.dateLabel}
                onChange={text("date")}
                type="date"
                value={input.date}
              />
            </div>
            <button
              className={BUTTON_SMALL}
              onClick={() => set("date")(localIsoDate())}
              type="button"
            >
              {strings.today}
            </button>
          </div>
        </Panel>
        <Panel title={strings.recipientHeading}>
          {field(
            "recipientName",
            strings.recipientNameLabel,
            strings.recipientNamePlaceholder
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {field(
              "recipientTitle",
              strings.recipientTitleLabel,
              strings.recipientTitlePlaceholder
            )}
            {field(
              "recipientOrganization",
              strings.recipientOrganizationLabel,
              strings.recipientOrganizationPlaceholder
            )}
          </div>
          {area(
            "recipientAddress",
            strings.recipientAddressLabel,
            strings.recipientAddressPlaceholder,
            2
          )}
        </Panel>
        <Panel title={strings.salutationHeading}>
          <PillGroup
            label={strings.salutationKindLabel}
            name="fl-kind"
            onChange={set("salutationKind")}
            options={(["named", "unnamed"] as const).map((value) => ({
              value,
              label: strings.salutationKinds[value],
            }))}
            value={input.salutationKind}
          />
          {input.salutationKind === "named" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="fl-honorific"
                label={strings.honorificLabel}
                onChange={set("honorific")}
                options={HONORIFICS.map((value) => ({
                  value,
                  label: strings.honorifics[value],
                }))}
                value={input.honorific}
              />
              {field(
                "salutationName",
                strings.salutationNameLabel,
                strings.salutationNamePlaceholder,
                strings.salutationNameHint
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="fl-unnamed"
                label={strings.unnamedLabel}
                onChange={set("unnamed")}
                options={UNNAMED_KINDS.map((value) => ({
                  value,
                  label: strings.unnamedKinds[value],
                }))}
                value={input.unnamed}
              />
              {input.unnamed === "role"
                ? field("role", strings.roleLabel, strings.rolePlaceholder)
                : null}
            </div>
          )}
          {input.format === "uk" ? (
            <Checkbox
              checked={input.openPunctuation}
              label={strings.openPunctuationLabel}
              onChange={set("openPunctuation")}
            />
          ) : (
            <SelectField
              id="fl-signoff"
              label={strings.signoffLabel}
              onChange={set("usSignoff")}
              options={US_SIGNOFFS.map((value) => ({
                value,
                label: strings.usSignoffs[value],
              }))}
              value={input.usSignoff}
            />
          )}
          <Notice>{signoffNote(input, strings)}</Notice>
        </Panel>
        <Panel title={strings.messageHeading}>
          {field("subject", strings.subjectLabel, strings.subjectPlaceholder)}
          {area("opening", strings.openingLabel, strings.paragraphPlaceholder)}
          {area(
            "details",
            strings.detailsLabel,
            strings.paragraphPlaceholder,
            4
          )}
          {area("closing", strings.closingLabel, strings.paragraphPlaceholder)}
          <div className="grid gap-4 sm:grid-cols-2">
            {field(
              "enclosures",
              strings.enclosuresLabel,
              strings.enclosuresPlaceholder
            )}
            {field("copies", strings.copiesLabel, strings.copiesPlaceholder)}
          </div>
        </Panel>
      </div>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <h2 className={WIDGET_H2}>{strings.letterHeading}</h2>
          <div className="flex flex-wrap gap-2">
            <CopyButton
              copiedLabel={strings.copied}
              html={letterToHtml(blocks)}
              label={strings.copy}
              small
              text={plain}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyPlain}
              small
              text={plain}
            />
            <button
              className={BUTTON_SMALL}
              onClick={() => downloadText(strings.fileName, plain)}
              type="button"
            >
              {strings.download}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() =>
                printHtmlDocument(
                  letterToPrintHtml(blocks, strings.printTitle, locale)
                )
              }
              type="button"
            >
              {strings.print}
            </button>
          </div>
        </div>
        <div aria-live="polite" className="space-y-1 print:hidden">
          <p className={HINT}>
            {blanks === 0
              ? strings.blanksNone
              : tPlural(strings.blanksLeft, locale, blanks, {
                  count: fmt(blanks),
                })}
          </p>
          <p className={HINT}>
            {tPlural(strings.lengthStatus, locale, words, {
              count: fmt(words),
            })}
          </p>
          {words > ONE_PAGE_BODY_WORDS ? (
            <Notice>{strings.lengthLong}</Notice>
          ) : null}
        </div>
        <article className="space-y-3 rounded-lg border border-border bg-card p-6 font-serif text-foreground text-sm leading-relaxed sm:p-10 print:border-0 print:p-0">
          {blocks.map((block, blockIndex) => (
            <div
              className={block.indent ? "ps-[40%]" : undefined}
              // Blocks are positional: the letter is rebuilt on every keystroke.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional blocks
              key={blockIndex}
            >
              {block.lines.map((line, lineIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
                <p className="min-h-[1.5em] break-words" key={lineIndex}>
                  <MarkedText text={line} />
                </p>
              ))}
            </div>
          ))}
        </article>
      </section>
    </div>
  );
}
